/**
 * One body row of a `RosterTable` (BON-538).
 *
 * The whole row is the link when the caller supplies an href — a roster is
 * scanned and then clicked, and a target confined to the name column makes a
 * reader aim at eleven characters. The link wraps the row rather than each
 * cell so there is ONE tab stop per row, not one per column.
 *
 * `highlighted` is separate from `:hover` on purpose: it is how a SIBLING
 * surface points at this row — the Venues map tints the row under the pin the
 * cursor is over — and a CSS hover cannot be driven from outside the element.
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

import { ROSTER_CELL_STYLE, type RosterColumnDef } from './roster-table-types';

export interface RosterTableRowProps<Row> {
  row: Row;
  columns: readonly RosterColumnDef<Row>[];
  template: string;
  /** Where the row goes when clicked. Omit for a row that is not a link. */
  href?: string;
  /** Picked out, because something elsewhere is pointing at this row. */
  highlighted?: boolean;
  /** Fired on pointer enter and leave, so a sibling surface can follow along. */
  onHoverChange?: (hovered: boolean) => void;
}

const ROW: CSSProperties = {
  display: 'grid',
  alignItems: 'stretch',
  minHeight: 52,
  borderTop: '1px solid var(--nb-border)',
  color: 'inherit',
  textDecoration: 'none',
  transition: 'background-color 120ms ease',
};

export function RosterTableRow<Row>({
  row,
  columns,
  template,
  href,
  highlighted = false,
  onHoverChange,
}: RosterTableRowProps<Row>) {
  const cells = columns.map((column) => (
    <div
      key={column.id}
      role="cell"
      style={{
        ...ROSTER_CELL_STYLE,
        justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
        color: column.muted ? 'var(--nb-subtle)' : undefined,
      }}
    >
      {column.render(row)}
    </div>
  ));

  const style: CSSProperties = {
    ...ROW,
    gridTemplateColumns: template,
    background: highlighted ? 'var(--nb-primary-soft)' : undefined,
  };

  const hover = onHoverChange && {
    onMouseEnter: () => onHoverChange(true),
    onMouseLeave: () => onHoverChange(false),
  };

  if (!href) {
    return (
      <div role="row" style={style} {...hover}>
        {cells}
      </div>
    );
  }

  return (
    <Link to={href} role="row" style={style} {...hover}>
      {cells}
    </Link>
  );
}
